// frontend/src/components/athleteEditor/SeasonStatsEditor.jsx
//
// The position × class-year game-stat grid. Extracted verbatim from
// GameStatsPage.js so the admin edit panel can mount it too — staff could read
// an athlete's season stats on the admin page but never enter or correct one.
//
// Addressing comes from the optional `getConfig` prop (see editorConfig.js).
// `positions` is passed in rather than fetched: both callers already hold the
// athlete's profile, so re-fetching it here would be a second round-trip for
// data the parent has.
//
// Sports whose "stats" are not position-based (track marks, result entries,
// match records, equestrian show results) never reach this component — the
// caller routes them to their own ledger off the catalog's stats_mode.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import { resolveGetConfig } from './editorConfig';

// year_offset is canonical: 0=Freshman … 3=Senior (matches the iOS editor —
// the source of truth — and the backend model). Labels MUST be in offset order
// so column index i lines up with year_offset i; they were reversed once,
// which stored and displayed every stat under the wrong class year vs iOS.
const YEAR_LABELS = ['Freshman', 'Sophomore', 'Junior', 'Senior'];

// Resolve a stored position string against the sport catalog the way Android's
// gameStatsForStoredPosition does: slug first, then label. Rows store either
// (iOS/web write the label, some Android rows hold the slug).
function resolveCatalogPosition(sports, sportSlug, stored) {
  const sport = (sports || []).find((s) => s.slug === sportSlug);
  const list = (sport && sport.positions) || [];
  return list.find((p) => p.slug === stored) || list.find((p) => p.label === stored) || null;
}

export default function SeasonStatsEditor({ sportSlug, positions = [], getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const getConfig = resolveGetConfig(getConfigProp);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [localValues, setLocalValues] = useState({});
  // What the server holds per cell (as loaded, then as last saved). Blur posts only a change:
  // tabbing through an empty box must not create a blank row, which the MaxPreps import
  // treats as the family's and never fills.
  const savedValues = useRef({});

  // getConfig is rebuilt every render by the caller, so it is deliberately not
  // a dependency — depending on it would refetch on every parent render.
  const fetchGameStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/game-stats/me`, getConfig());
      const vals = {};
      for (const s of response.data || []) {
        vals[`${s.position_slot}_${s.year_offset}_${s.stat_name}`] = s.stat_value || '';
      }
      savedValues.current = { ...vals };
      setLocalValues(vals);
    } catch (error) {
      console.log('Error fetching game stats:', error);
    }
    // eslint-disable-next-line
  }, [API_URL]);

  useEffect(() => { fetchGameStats(); }, [fetchGameStats]);

  const showMsg = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const getStatValue = (posSlot, yearOffset, statName) =>
    localValues[`${posSlot}_${yearOffset}_${statName}`] || '';

  const handleChange = (posSlot, yearOffset, statName, value) => {
    setLocalValues(prev => ({ ...prev, [`${posSlot}_${yearOffset}_${statName}`]: value }));
  };

  const handleBlur = useCallback(async (posSlot, yearOffset, statName, position) => {
    const cellKey = `${posSlot}_${yearOffset}_${statName}`;
    const value = localValues[cellKey] || '';
    if (value === (savedValues.current[cellKey] || '')) return;
    try {
      await axios.post(`${API_URL}/game-stats/bulk`, {
        stats: [{
          position,
          position_slot: posSlot,
          year_offset: yearOffset,
          stat_name: statName,
          stat_value: value,
        }],
      }, getConfig());
      savedValues.current[cellKey] = value;
      showMsg('Saved');
    } catch (error) {
      showMsg('Error saving', 'error');
    }
    // eslint-disable-next-line
  }, [localValues, API_URL]);

  if (positions.length === 0) {
    return (
      <div className="card">
        <div className="empty-state-card">
          <div className="empty-state-card__icon">📊</div>
          <div>
            No positions selected yet. Set the athlete&apos;s positions first, then
            come back to enter game stats.
          </div>
        </div>
      </div>
    );
  }

  const currentPosition = positions[activeTab] || positions[0];
  const posSlot = activeTab + 1;
  const labelFor = (stored) => {
    const p = resolveCatalogPosition(catalog.sports, sportSlug, stored);
    return p ? p.label : stored;
  };
  const catalogPosition = resolveCatalogPosition(catalog.sports, sportSlug, currentPosition);
  const currentLabel = labelFor(currentPosition);
  // Games Played is the fixed highlighted row above, so the catalog's own copy
  // (several sports list it) is dropped to avoid two inputs with one label.
  const statFields = ((catalogPosition && catalogPosition.game_stats) || [])
    .map((g) => g.label)
    .filter((label) => label !== 'Games Played');

  const cell = (statName, yearIdx) => (
    <td key={yearIdx} style={{ padding: '4px 8px', textAlign: 'center' }}>
      <input
        type="text"
        className="form-field__input"
        value={getStatValue(posSlot, yearIdx, statName)}
        onChange={(e) => handleChange(posSlot, yearIdx, statName, e.target.value)}
        onBlur={() => handleBlur(posSlot, yearIdx, statName, currentPosition)}
        aria-label={`${statName} ${YEAR_LABELS[yearIdx]}`}
        style={{
          textAlign: 'center', padding: '4px 8px', fontSize: '13px',
          maxWidth: '100px', margin: '0 auto',
          ...(statName === 'Games Played' ? { fontWeight: 600 } : null),
        }}
      />
    </td>
  );

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {positions.length > 1 && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
          {positions.map((pos, idx) => (
            <button
              key={pos}
              className={`nav-link ${idx === activeTab ? 'nav-link--active' : ''}`}
              onClick={() => setActiveTab(idx)}
              style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '14px' }}
            >
              {labelFor(pos)}
            </button>
          ))}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">{currentLabel} Stats</h2>
        <p className="settings-section__desc">
          Enter game statistics for each year. Data saves automatically on blur.
        </p>

        {statFields.length === 0 ? (
          <p style={{ color: '#64748b' }}>No stat fields defined for this position.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569' }}>
                    Stat
                  </th>
                  {YEAR_LABELS.map((label, yearIdx) => (
                    <th key={yearIdx} style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#475569', minWidth: '100px' }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Games Played row — highlighted */}
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f0f7ff' }}>
                  <td style={{ padding: '6px 12px', fontWeight: 600, color: '#1e40af' }}>
                    Games Played
                  </td>
                  {YEAR_LABELS.map((_, yearIdx) => cell('Games Played', yearIdx))}
                </tr>
                <tr><td colSpan={YEAR_LABELS.length + 1} style={{ height: '12px' }} /></tr>
                {statFields.map((statName) => (
                  <tr key={statName} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 12px', fontWeight: 500, color: '#334155' }}>
                      {statName}
                    </td>
                    {YEAR_LABELS.map((_, yearIdx) => cell(statName, yearIdx))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
